import type { Lesson } from "@/core/types";
import { nutztFunktion } from "@/office/excel/formeln";
import { formelAufgabe } from "../formelaufgabe";

export const lektionLogik: Lesson = {
  id: "logik",
  title: "Mehrere Bedingungen: UND, ODER, WENNS und die WENNS-Familie",
  summary: "Wenn eine Bedingung nicht reicht – ohne WENN in WENN in WENN.",
  durationMinutes: 13,
  learningGoals: [
    "Bedingungen mit UND und ODER verknüpfen",
    "Mehrere Fälle mit WENNS oder ERSTERWERT unterscheiden",
    "Mit SUMMEWENNS, ZÄHLENWENNS und MITTELWERTWENNS nach mehreren Kriterien auswerten",
  ],
  steps: [
    {
      id: "und-oder",
      title: "UND und ODER",
      explanation: [
        "UND(Bedingung1;Bedingung2) ist wahr, wenn alle Bedingungen zutreffen, ODER, wenn " +
          "mindestens eine zutrifft. Allein liefern sie nur WAHR oder FALSCH – ihre Stärke " +
          "zeigen sie als Prüfung in WENN: =WENN(UND(B2>=1000;C2=\"Nord\");\"Bonus\";\"\").",
        "Das ist lesbarer und weniger fehleranfällig als zwei ineinander geschachtelte " +
          "WENN-Funktionen, die dasselbe tun.",
      ],
      keyPoints: [
        "UND: alle müssen zutreffen. ODER: eine genügt.",
        "=WENN(UND(…;…);Dann;Sonst).",
      ],
      task: {
        instruction: "Schreibe eine WENN-Formel, deren Prüfung zwei Bedingungen mit UND oder ODER verknüpft.",
        hint: "=WENN(UND(B2>=1000;C2=\"Nord\");\"Bonus\";\"\")",
        check: formelAufgabe(
          [
            { label: "WENN", funktionen: ["wenn"] },
            { label: "UND oder ODER", funktionen: ["und", "oder"] },
          ],
          {
            success: "Zwei Bedingungen, eine Formel – ohne Verschachtelung.",
            failure: "Es gibt noch keine WENN-Formel mit UND oder ODER.",
          },
          true,
        ),
      },
    },
    {
      id: "wenns",
      title: "WENNS und ERSTERWERT",
      explanation: [
        "Für mehr als zwei Fälle gibt es WENNS: Paare aus Prüfung und Ergebnis, " +
          "abgearbeitet von links nach rechts. =WENNS(B2>=10000;\"A\";B2>=1000;\"B\";WAHR;\"C\") " +
          "– das letzte WAHR fängt alle übrigen Fälle ab.",
        "ERSTERWERT vergleicht einen Wert mit einer Liste: =ERSTERWERT(C2;\"N\";\"Nord\";" +
          "\"S\";\"Süd\";\"unbekannt\"). Das ist die kompakte Form für feste Zuordnungen.",
        "Wie schon im Grundlagenmodul gilt: Ab fünf, sechs Fällen oder wenn sich die " +
          "Zuordnung ändert, ist eine kleine Zuordnungstabelle mit SVERWEIS oder XVERWEIS " +
          "die bessere Wahl.",
      ],
      keyPoints: [
        "WENNS(Prüfung1;Wert1;Prüfung2;Wert2;…;WAHR;Rest).",
        "ERSTERWERT(Wert;Fall1;Ergebnis1;…;Standard).",
        "Viele Fälle? Zuordnungstabelle.",
      ],
      task: {
        instruction: "Teile Werte mit WENNS oder ERSTERWERT in mindestens drei Klassen ein.",
        hint: "=WENNS(B2>=10000;\"A\";B2>=1000;\"B\";WAHR;\"C\")",
        // Drei Fälle brauchen mindestens sechs Argumente, also fünf Trennzeichen.
        check: formelAufgabe(
          [
            {
              label: "WENNS oder ERSTERWERT mit mindestens drei Fällen",
              funktionen: ["wenns", "ersterwert"],
              test: (formel) => (formel.match(/[;,]/g) ?? []).length >= 5,
            },
          ],
          {
            success: "Mehrere Klassen, eine lesbare Formel.",
            failure: "Es gibt noch keine WENNS- oder ERSTERWERT-Formel mit drei Fällen.",
            hint: "Drei Fälle brauchen bei WENNS sechs Argumente – drei Prüfungen, drei Ergebnisse.",
          },
        ),
      },
    },
    {
      id: "mehrere-kriterien",
      title: "SUMMEWENNS, ZÄHLENWENNS, MITTELWERTWENNS",
      explanation: [
        "Die WENNS-Familie wertet nach beliebig vielen Kriterien aus. Anders als bei " +
          "SUMMEWENN steht der Summenbereich hier vorn: =SUMMEWENNS(D:D;B:B;\"Nord\";C:C;" +
          "\">=1000\") – Summe der Beträge aus Nord ab 1000.",
        "ZÄHLENWENNS und MITTELWERTWENNS funktionieren genauso. Mit Bezügen auf " +
          "Kriterienzellen statt getippten Werten entsteht daraus eine kleine " +
          "Auswertungsmaske: Region und Mindestbetrag oben eintragen, das Ergebnis " +
          "rechnet sich neu.",
      ],
      keyPoints: [
        "SUMMEWENNS(Summenbereich;Bereich1;Kriterium1;Bereich2;Kriterium2;…).",
        "Der Summenbereich steht vorn – anders als bei SUMMEWENN.",
        "Kriterien in Zellen statt in der Formel.",
      ],
      task: {
        instruction: "Werte deine Daten mit SUMMEWENNS, ZÄHLENWENNS oder MITTELWERTWENNS nach zwei Kriterien aus.",
        hint: "=ZÄHLENWENNS(B:B;\"Nord\";D:D;\">=1000\")",
        check: formelAufgabe(
          [
            {
              label: "Eine WENNS-Auswertung mit mindestens zwei Kriterien",
              funktionen: ["summewenns", "zaehlenwenns", "mittelwertwenns"],
              test: (formel) =>
                nutztFunktion(formel, "zaehlenwenns")
                  ? (formel.match(/[;,]/g) ?? []).length >= 3
                  : (formel.match(/[;,]/g) ?? []).length >= 4,
            },
          ],
          {
            success: "Eine Auswertung nach mehreren Kriterien – ohne Hilfsspalten.",
            failure: "Es gibt noch keine Auswertung mit mindestens zwei Kriterien.",
          },
        ),
      },
    },
  ],
};
